/**
 * 🗂️ Project Plan Types
 *
 * A Plan is the intermediate container between a Project and its assets
 * (actions, issues, blueprints/executions).
 */

export type PlanType = 'action' | 'process' | 'improvement';

export const PLAN_TYPE_LABELS: Record<PlanType, string> = {
  action: 'Plano de Ação',
  process: 'Processo',
  improvement: 'Melhoria',
};

export const PLAN_TYPE_COLORS: Record<PlanType, string> = {
  action: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  process: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  improvement: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
};

export interface ProjectPlan {
  id: string;
  organization_id: string;
  project_id: string | null;
  name: string;
  type: PlanType;
  description?: string | null;
  metadata?: { team_members?: string[]; [key: string]: unknown } | null;
  created_at: string;
  updated_at: string;
}

export interface PlanMetrics {
  plan_id: string;
  project_id: string;
  organization_id: string;
  type: PlanType;
  // Actions
  actions_total: number;
  actions_completed: number;
  actions_open: number;
  actions_overdue: number;
  // Issues
  issues_total: number;
  issues_resolved: number;
  issues_open: number;
  issues_critical: number;
  // Executions
  executions_total: number;
  executions_completed: number;
  executions_active: number;
  executions_sla_risk: number;
}

/** Aggregated metrics for a project (sum of all its plans) */
export interface ProjectPlansSummary {
  total_plans: number;
  actions_overdue: number;
  actions_total: number;
  actions_completed: number;
  issues_critical: number;
  issues_total: number;
  issues_resolved: number;
  executions_active: number;
  executions_total: number;
  executions_completed: number;
  executions_sla_risk: number;
  progress: number;
}

export type ProjectHealthStatus = 'critical' | 'warning' | 'healthy';

/** Computed plan progress */
export interface PlanProgress {
  plan_id: string;
  type: PlanType;
  progress: number; // 0-100
}
